/**
 * Interactive product tour — chapters, steps, hotspots, click sequences.
 * Roles: admin | tenant_admin | user | all
 */

export const TOUR_CHAPTERS = [
  {
    id: "welcome",
    title: "Welcome & Overview",
    subtitle: "What OneDPDP is and who does what",
    icon: "sparkles",
    order: 1,
  },
  {
    id: "dashboard",
    title: "Your Home Screen",
    subtitle: "Find your way around and see key numbers",
    icon: "layout",
    order: 2,
  },
  {
    id: "modules",
    title: "Feature Walkthrough",
    subtitle: "Every tool explained in simple steps",
    icon: "layers",
    order: 3,
  },
  {
    id: "journey",
    title: "Your Compliance Journey",
    subtitle: "How everything fits together from start to finish",
    icon: "route",
    order: 4,
  },
  {
    id: "tasks",
    title: "Quick How-To Guides",
    subtitle: "Log in, add users, submit forms — fast",
    icon: "checklist",
    order: 5,
  },
  {
    id: "faq",
    title: "Help When You're Stuck",
    subtitle: "Common questions and fixes",
    icon: "help",
    order: 6,
  },
];

export const TOUR_STEPS = [
  // ── WELCOME ──────────────────────────────────────────────────────────────
  {
    id: "w1-user",
    chapterId: "welcome",
    roles: ["user"],
    pageName: "OneDPDP Platform",
    purpose: "Understand what OneDPDP does and how your navigation bar works.",
    frame: "welcome",
    frameLabel: "User",
    title: "Welcome to OneDPDP",
    description:
      "OneDPDP is your team's home for DPDP Act compliance — no legal or IT background needed. As a team member, use the top menu for Home, Dashboard, Assessments, Breach Management, Consent Variance, DSAR, Policy Store, Reports, and Help.",
    hotspots: [
      { id: "h1", x: 1, y: 5, w: 11, h: 90, label: "OneDPDP Logo", tooltip: "Your brand home — click to return to your Home page." },
      { id: "h2", x: 12, y: 5, w: 72, h: 90, label: "Navigation Bar", tooltip: "All modules available to the User role." },
      { id: "h3", x: 85, y: 5, w: 14, h: 90, label: "Notifications, Profile & Logout", tooltip: "View your name, role, and sign out." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Identify the OneDPDP logo in the top-left of every page.", outcome: "Logo always returns you to Home." },
      { step: 2, hotspotId: "h2", action: "Scan the navigation bar — this is everything a User can access.", outcome: "You know where each feature lives before diving in." },
      { step: 3, hotspotId: "h3", action: "Check your profile area to confirm you're logged in with the correct role.", outcome: "Avoid working in the wrong account or organisation." },
    ],
    tips: ["Bookmark your organisation's login link — you'll need it every time you sign in."],
    warnings: ["Never share your password. Your Tenant Admin resets credentials from the Users page."],
    expectedOutcome: "You understand the platform layout and your role in the compliance workflow.",
    nextHint: "Next: learn about the three user roles.",
    livePath: null,
  },
  {
    id: "w1-tenant",
    chapterId: "welcome",
    roles: ["tenant_admin"],
    pageName: "OneDPDP Platform",
    purpose: "Understand what OneDPDP does and how your navigation bar works.",
    frame: "welcome",
    frameLabel: "Tenant Admin",
    title: "Welcome to OneDPDP",
    description:
      "OneDPDP helps your organisation get ready for the DPDP Act. As Organisation Admin, you set up departments and users, run assessment cycles, and review what your team submits — all from the top menu.",
    hotspots: [
      { id: "h1", x: 1, y: 5, w: 11, h: 90, label: "OneDPDP Logo", tooltip: "Your brand home — click to return to the Tenant Dashboard." },
      { id: "h2", x: 11, y: 5, w: 36, h: 90, label: "Navigation Bar", tooltip: "Dashboard, and the Tenant Admins dropdown (Users, Departments, Gap Admin, Form Questions)." },
      { id: "h3", x: 47, y: 5, w: 35, h: 90, label: "License & Settings", tooltip: "License usage and organisation-wide settings." },
      { id: "h4", x: 85, y: 5, w: 14, h: 90, label: "Notifications, Profile & Logout", tooltip: "View your name, role, and sign out." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Identify the OneDPDP logo in the top-left of every page.", outcome: "Logo always returns you to the Tenant Dashboard." },
      { step: 2, hotspotId: "h2", action: "Open Tenant Admins to reach Users, Departments, Gap Admin, and Form Questions.", outcome: "Every org-setup tool lives under this one dropdown." },
      { step: 3, hotspotId: "h3", action: "Check License for your user-slot usage and Settings for SMTP/branding.", outcome: "You know where org-wide configuration lives." },
      { step: 4, hotspotId: "h4", action: "Check your profile area to confirm you're logged in with the correct role.", outcome: "Avoid working in the wrong organisation." },
    ],
    tips: ["Bookmark your organisation's login link — you'll need it every time you sign in."],
    warnings: ["Never share your password. Reset credentials for your team from the Users page."],
    expectedOutcome: "You understand the platform layout and your role in the compliance workflow.",
    nextHint: "Next: learn about the three user roles.",
    livePath: null,
  },
  {
    id: "w1-admin",
    chapterId: "welcome",
    roles: ["admin"],
    pageName: "OneDPDP Platform",
    purpose: "Understand what OneDPDP does and who uses it.",
    frame: "welcome",
    title: "Welcome to OneDPDP",
    description:
      "OneDPDP is the platform your customer organisations use for DPDP compliance. As Super Admin, you create organisations, set their plans, turn features on or off, and keep email working for everyone.",
    hotspots: [
      { id: "h1", x: 8, y: 4, w: 18, h: 6, label: "OneDPDP Logo", tooltip: "Your brand home — click to return to the Super Admin dashboard." },
      { id: "h2", x: 20, y: 4, w: 55, h: 6, label: "Navigation Bar", tooltip: "Tenants, licenses, and platform settings." },
      { id: "h3", x: 78, y: 4, w: 18, h: 6, label: "Profile & Logout", tooltip: "View your name, role, and sign out." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Identify the OneDPDP logo in the top-left of every page.", outcome: "Logo always returns you to the Super Admin dashboard." },
      { step: 2, hotspotId: "h2", action: "Scan the navigation bar to see platform-wide tools.", outcome: "You know where tenant management and platform config live." },
      { step: 3, hotspotId: "h3", action: "Check your profile area to confirm you're logged in with the correct role.", outcome: "Avoid making changes in the wrong context." },
    ],
    tips: ["Super Admin uses the platform login — you operate above all organisations."],
    warnings: ["Changes here (licenses, module toggles) affect entire tenant organisations."],
    expectedOutcome: "You understand the platform layout and your role in the compliance workflow.",
    nextHint: "Next: learn about the three user roles.",
    livePath: null,
  },
  {
    id: "w2",
    chapterId: "welcome",
    roles: ["all"],
    pageName: "User Roles",
    purpose: "Learn which role you have and what you can access.",
    frame: "roles",
    title: "Three Roles, One Platform",
    description:
      "Super Admin runs the whole product. Organisation Admin sets up one company. Team Members do the day-to-day compliance work. Your menu shows only what your role needs.",
    hotspots: [
      { id: "h1", x: 4, y: 16, w: 29, h: 79, label: "Super Admin", tooltip: "Creates tenants, sets licenses, configures platform SMTP. Logs in at the platform login URL." },
      { id: "h2", x: 35, y: 16, w: 29, h: 79, label: "Tenant Admin", tooltip: "Manages users, departments, form questions, and assessment cycles for one organisation." },
      { id: "h3", x: 67, y: 16, w: 29, h: 79, label: "End User", tooltip: "Completes Gap/ROPA/PIA/TPRM, reports breaches, and generates compliance reports." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Super Admin: log in at the platform login URL.", outcome: "Access /admin — tenants, licenses, settings." },
      { step: 2, hotspotId: "h2", action: "Tenant Admin: log in with the organisation login link.", outcome: "Access /tenant — users, departments, gap admin." },
      { step: 3, hotspotId: "h3", action: "End User: log in with the organisation login link.", outcome: "Access /user — assessments, breach, reports." },
    ],
    tips: ["Your role is shown next to your name in the header."],
    warnings: ["Using the wrong organisation login link causes 'Invalid credentials' even with the correct password."],
    expectedOutcome: "You know your role and which navigation items you'll see.",
    nextHint: "Next: tour your dashboard.",
    livePath: "/login",
  },
  {
    id: "w3-admin",
    chapterId: "welcome",
    roles: ["admin"],
    pageName: "Signing In — Super Admin",
    purpose: "Recognise your login screen and credentials pattern.",
    frame: "login",
    frameLabel: "Admin",
    title: "Your Login Screen — Super Admin",
    description: "Super Admin signs in with just email and password at the platform login URL.",
    hotspots: [
      { id: "h1", x: 61, y: 33, w: 28, h: 8, label: "Email", tooltip: "Platform email, e.g. admin@demo.com — no organisation login link needed." },
      { id: "h2", x: 61, y: 44, w: 28, h: 8, label: "Password", tooltip: "Your case-sensitive password." },
      { id: "h3", x: 61, y: 55, w: 28, h: 8, label: "Captcha", tooltip: "Complete the 'I'm not a robot' check." },
      { id: "h4", x: 61, y: 65, w: 28, h: 8, label: "Sign In", tooltip: "Submit to enter your workspace." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Enter your Super Admin email.", outcome: "You manage the whole platform from this login." },
      { step: 2, hotspotId: "h2", action: "Enter your password.", outcome: "Credentials ready to submit." },
      { step: 3, hotspotId: "h3", action: "Complete the captcha check.", outcome: "Bot protection cleared." },
      { step: 4, hotspotId: "h4", action: "Click Sign In.", outcome: "You land on the Super Admin dashboard." },
    ],
    tips: ["If you land on a tenant login by mistake, use the platform login URL instead."],
    warnings: [],
    expectedOutcome: "You can identify and use the Super Admin login screen.",
    nextHint: "Next: tour your dashboard.",
    livePath: "/login",
  },
  {
    id: "w3-tenant",
    chapterId: "welcome",
    roles: ["tenant_admin"],
    pageName: "Signing In — Tenant Admin",
    purpose: "Recognise your login screen and credentials pattern.",
    frame: "login",
    frameLabel: "Tenant Admin",
    title: "Your Login Screen — Tenant Admin",
    description: "Tenant Admin signs in with your organisation login link, plus your email and password.",
    hotspots: [
      { id: "h1", x: 61, y: 33, w: 28, h: 8, label: "Email", tooltip: "Your org email, e.g. tenantadmin@demo.com." },
      { id: "h2", x: 61, y: 44, w: 28, h: 8, label: "Password", tooltip: "Your case-sensitive password." },
      { id: "h3", x: 61, y: 55, w: 28, h: 8, label: "Captcha", tooltip: "Complete the 'I'm not a robot' check." },
      { id: "h4", x: 61, y: 65, w: 28, h: 8, label: "Sign In", tooltip: "Submit to enter your workspace." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Enter your Tenant Admin email.", outcome: "Make sure you're on your organisation's login link." },
      { step: 2, hotspotId: "h2", action: "Enter your password.", outcome: "Credentials ready to submit." },
      { step: 3, hotspotId: "h3", action: "Complete the captcha check.", outcome: "Bot protection cleared." },
      { step: 4, hotspotId: "h4", action: "Click Sign In.", outcome: "You land on the Tenant Admin dashboard." },
    ],
    tips: ["Wrong organisation link = 'Invalid credentials' even with the right password."],
    warnings: [],
    expectedOutcome: "You can identify and use the Tenant Admin login screen.",
    nextHint: "Next: tour your dashboard.",
    livePath: "/login",
  },
  {
    id: "w3-user",
    chapterId: "welcome",
    roles: ["user"],
    pageName: "Signing In — User",
    purpose: "Recognise your login screen and credentials pattern.",
    frame: "login",
    frameLabel: "User",
    title: "Your Login Screen — User",
    description: "End Users sign in with your organisation's link, plus your email and password.",
    hotspots: [
      { id: "h1", x: 61, y: 33, w: 28, h: 8, label: "Email", tooltip: "Your work email, e.g. user@demo.com." },
      { id: "h2", x: 61, y: 44, w: 28, h: 8, label: "Password", tooltip: "Your case-sensitive password." },
      { id: "h3", x: 61, y: 55, w: 28, h: 8, label: "Captcha", tooltip: "Complete the 'I'm not a robot' check." },
      { id: "h4", x: 61, y: 65, w: 28, h: 8, label: "Sign In", tooltip: "Submit to enter your workspace." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Enter your work email.", outcome: "Make sure you're on your organisation's login link." },
      { step: 2, hotspotId: "h2", action: "Enter your password.", outcome: "Credentials ready to submit." },
      { step: 3, hotspotId: "h3", action: "Complete the captcha check.", outcome: "Bot protection cleared." },
      { step: 4, hotspotId: "h4", action: "Click Sign In.", outcome: "You land on your Home page." },
    ],
    tips: ["Forgot your password? Ask your Tenant Admin to reset it from the Users page."],
    warnings: [],
    expectedOutcome: "You can identify and use your login screen.",
    nextHint: "Next: tour your Home page.",
    livePath: "/login",
  },

  // ── DASHBOARD (role-specific) ───────────────────────────────────────────
  {
    id: "d-user-1",
    chapterId: "dashboard",
    roles: ["user"],
    pageName: "Home",
    purpose: "Your personalised compliance roadmap and next-action hub — the first screen after login.",
    frame: "user-home",
    title: "Home — Your Compliance Starting Point",
    description: "Home opens with your progress snapshot, the Road to Compliance journey timeline, and Smart Recommendations for what to do next. (Home is not the Dashboard — the Dashboard, up next, shows detailed stats for every module.)",
    hotspots: [
      { id: "h1", x: 3, y: 5, w: 94, h: 12, label: "Progress Snapshot", tooltip: "Start date, % progress, DPDP deadline, and phases done — your status at a glance." },
      { id: "h2", x: 3, y: 17, w: 94, h: 34, label: "Road to Compliance", tooltip: "Your 5-phase DPDP journey — Assess, PIA, ROPA, TPRM, Breach — with per-phase status and days remaining." },
      { id: "h3", x: 3, y: 52, w: 94, h: 46, label: "Smart Recommendations", tooltip: "Prioritised next actions based on your progress and the compliance deadline." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Check the Progress and Deadline capsules in the header.", outcome: "You know overall % complete and days left until 13 May 2027." },
      { step: 2, hotspotId: "h2", action: "Look at the Road to Compliance timeline — Assess, PIA, ROPA, TPRM, Breach.", outcome: "See which phase you're in and how many departments/users are done." },
      { step: 3, hotspotId: "h3", action: "Read the Smart Recommendations card for your priority task.", outcome: "Know exactly what to work on next without guessing." },
    ],
    tips: ["Home ≠ Dashboard — Home shows your journey and next steps; the Dashboard (next) shows detailed stats per module.", "Visit Home daily — recommendations update as you complete modules."],
    warnings: [],
    expectedOutcome: "You can orient yourself and pick your next compliance task.",
    nextHint: "Next: the Dashboard shows detailed stats for every module.",
    livePath: "/user",
  },
  {
    id: "d-user-2",
    chapterId: "dashboard",
    roles: ["user"],
    pageName: "Dashboard",
    purpose: "View consent, DSAR, DSPM, ROPA, and every other module's stats at a glance.",
    frame: "user-dashboard",
    title: "Dashboard — All Modules at a Glance",
    description: "Dashboard shows a live snapshot of every module — consent, cookie, DSAR, ROPA, Gap Assessment, DSPM, DPIA, Third Parties, and Privacy Notices — plus Quick Actions to jump straight into a task.",
    hotspots: [
      { id: "h1", x: 3, y: 13, w: 94, h: 10, label: "KPI Summary", tooltip: "Organization status, active users, data requests, and overall compliance score." },
      { id: "h2", x: 3, y: 24, w: 94, h: 18, label: "Consent & Request Modules", tooltip: "User Consent, Cookie Consent, and DSAR — acceptance/rejection rates and processing status." },
      { id: "h3", x: 3, y: 43, w: 94, h: 18, label: "Compliance Modules", tooltip: "ROPA, Gap Assessment, and DSPM — completion, score, and discovered PII counts." },
      { id: "h4", x: 3, y: 62, w: 94, h: 18, label: "Risk & Privacy Modules", tooltip: "DPIA, Third Parties, and Privacy Notices — sensitive data and vendor risk." },
      { id: "h5", x: 3, y: 81, w: 94, h: 10, label: "Quick Actions", tooltip: "Jump straight into creating a DPIA, ROPA record, gap assessment, vendor review, or report." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Check your Compliance Score tile.", outcome: "See overall % and whether critical gaps exist." },
      { step: 2, hotspotId: "h2", action: "Click the DSAR tile to expand pending requests.", outcome: "See processed vs. pending data subject requests." },
      { step: 3, hotspotId: "h3", action: "Check Gap Assessment score and ROPA section progress.", outcome: "Identify which compliance modules need attention." },
      { step: 4, hotspotId: "h5", action: "Use Quick Actions to jump straight into a task.", outcome: "Skip navigation — start a DPIA, ROPA record, or report directly." },
    ],
    tips: ["Zero stats on a tile often mean that integration isn't enabled yet — ask your Tenant Admin.", "Use Quick Actions at the bottom to skip navigation entirely."],
    warnings: [],
    expectedOutcome: "You can monitor organisation-wide compliance metrics.",
    nextHint: "Next: module-by-module walkthrough begins.",
    livePath: "/user/dashboard",
  },
  {
    id: "d-tenant-1",
    chapterId: "dashboard",
    roles: ["tenant_admin"],
    pageName: "Tenant Dashboard",
    purpose: "Organisation overview — users, license usage, quick actions.",
    frame: "tenant-dashboard",
    title: "Tenant Admin Dashboard",
    description: "Monitor user count and license utilisation, then jump straight into common admin tasks via Quick Actions.",
    hotspots: [
      { id: "h1", x: 2, y: 16, w: 96, h: 12, label: "Key Stats", tooltip: "Total users, user limit, slots used, and license expiry." },
      { id: "h2", x: 2, y: 28, w: 96, h: 11, label: "Quick Actions", tooltip: "Jump straight to Add New User, Email Template, License Info, or Settings." },
      { id: "h3", x: 2, y: 40, w: 47, h: 35, label: "Recent Users", tooltip: "Latest users added to your organisation." },
      { id: "h4", x: 51, y: 40, w: 47, h: 48, label: "License Utilization", tooltip: "Slots used vs. plan limit, expiry, and status." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Check License Expiry and Slots Used at a glance.", outcome: "Avoid failed user creation at the limit." },
      { step: 2, hotspotId: "h2", action: "Use Quick Actions to add a user or edit settings.", outcome: "Reach key setup pages in one click." },
      { step: 3, hotspotId: "h4", action: "Check License Utilization for remaining slots.", outcome: "Plan ahead before hitting your user limit." },
    ],
    tips: ["Set up departments before users start assessments."],
    warnings: ["Creating users when at license limit returns a 403 error."],
    expectedOutcome: "You understand org capacity and can reach admin tools quickly.",
    nextHint: "Next: walk through each module.",
    livePath: "/tenant",
  },
  {
    id: "d-admin-1",
    chapterId: "dashboard",
    roles: ["admin"],
    pageName: "Admin Dashboard",
    purpose: "Platform-wide tenant, user, and license statistics.",
    frame: "admin-dashboard",
    title: "Super Admin Dashboard",
    description: "Overview of all tenants, total users, and license utilisation across the platform.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 30, h: 26, label: "Tenant Stats", tooltip: "Total and active tenants." },
      { id: "h2", x: 36, y: 18, w: 30, h: 26, label: "User Stats", tooltip: "Users across all tenants." },
      { id: "h3", x: 68, y: 18, w: 28, h: 26, label: "License Overview", tooltip: "Platform-wide license usage." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Review tenant count and status.", outcome: "Spot inactive or suspended tenants." },
      { step: 2, hotspotId: "h3", action: "Check license overview for tenants near limits.", outcome: "Proactively adjust limits before customers are blocked." },
    ],
    tips: ["Configure platform SMTP in Settings before onboarding tenants."],
    warnings: [],
    expectedOutcome: "You have a platform health snapshot.",
    nextHint: "Next: tenant management module.",
    livePath: "/admin",
  },

  // ── MODULES — USER ───────────────────────────────────────────────────────
  {
    id: "m-gap-1",
    chapterId: "modules",
    roles: ["user"],
    pageName: "Gap Assessment",
    purpose: "Baseline privacy maturity assessment by department and section.",
    frame: "gap-assessment",
    title: "Gap Assessment — Answer Questions",
    description: "Assessment → Gap Assessment. Select your department in the header, then use the left sidebar (10 DPDP sections) and answer Yes / No / Partial on the right. Progress auto-saves.",
    hotspots: [
      { id: "h1", x: 2, y: 25, w: 19, h: 70, label: "Section Sidebar", tooltip: "Compliance score, policy recommendations after No/Partial answers, and all 10 DPDP section tabs." },
      { id: "h2", x: 23, y: 25, w: 75, h: 16, label: "Section Banner", tooltip: "Current section title, DPDP code, question count, and section progress." },
      { id: "h3", x: 23, y: 42, w: 75, h: 52, label: "Questions", tooltip: "Yes / No / Partial, comments, owner assignment, and evidence attachments." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Select your department in the header, then pick a section (e.g. Consent Management).", outcome: "Questions for that section load on the right." },
      { step: 2, hotspotId: "h2", action: "Review the section description and progress bar.", outcome: "Understand what this DPDP domain covers." },
      { step: 3, hotspotId: "h3", action: "Answer each question; add comments and upload evidence where needed.", outcome: "Section progress and policy recommendations update live." },
    ],
    tips: ["Answers auto-save — no separate Save Draft button.", "Answering No or Partial surfaces matching Policy Store recommendations in the sidebar."],
    warnings: ["You need an open assessment cycle (set by Tenant Admin) to submit."],
    expectedOutcome: "You can navigate and fill the gap assessment form.",
    nextHint: "Next: submit your assessment.",
    livePath: "/user/gap-assessment",
  },
  {
    id: "m-gap-2",
    chapterId: "modules",
    roles: ["user"],
    pageName: "Gap Assessment — Submit",
    purpose: "Submit completed assessment or review submission history.",
    frame: "gap-assessment-submit",
    title: "Gap Assessment — Submit & History",
    description: "On the last section (Breach Management), Submit Assessment appears in the footer. View Table in the header opens submission history.",
    hotspots: [
      { id: "h1", x: 68, y: 88, w: 14, h: 8, label: "Previous / Dots", tooltip: "Navigate sections; dot stepper jumps to any of the 10 sections." },
      { id: "h2", x: 84, y: 88, w: 12, h: 8, label: "Submit Assessment", tooltip: "Final submission on the last section — locks answers for the cycle." },
      { id: "h3", x: 72, y: 8, w: 14, h: 8, label: "View Table", tooltip: "Past submissions, filters, and export." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Complete all 10 sections using Next in the footer.", outcome: "Each section validates required answers before advancing." },
      { step: 2, hotspotId: "h2", action: "On section 10, click Submit Assessment.", outcome: "Assessment locked; Tenant Admin can review and score." },
      { step: 3, hotspotId: "h3", action: "Open View Table to verify your submission.", outcome: "History and export available per department." },
    ],
    tips: ["Progress auto-saves as you work — no manual draft save needed.", "Check policy recommendations after marking gaps."],
    warnings: ["Incomplete required fields block submission."],
    expectedOutcome: "Assessment submitted and visible in submissions history.",
    nextHint: "Next: ROPA module.",
    livePath: "/user/gap-assessment/submissions",
  },
  {
    id: "m-ropa-1",
    chapterId: "modules",
    roles: ["user"],
    pageName: "ROPA",
    purpose: "Document Record of Processing Activities — lawful purposes, data categories, retention.",
    frame: "form-generic",
    frameLabel: "ROPA",
    frameColor: "#2563eb",
    title: "ROPA — Record Processing Activities",
    description: "Assessment → ROPA. Select a department, then use the 12-section sidebar and fill each register section on the right. Submit ROPA in the header when complete.",
    hotspots: [
      { id: "h1", x: 2, y: 23, w: 19, h: 72, label: "12-Section Sidebar", tooltip: "Completion gauge plus Organisation Details, Processing Activity, Legal Basis, Data Principals, Personal Data, Lifecycle, Sharing, Retention, Security, Rights, Incident, and Approval." },
      { id: "h2", x: 23, y: 23, w: 75, h: 72, label: "Section Fields", tooltip: "Register fields for the active section — required fields marked with *." },
      { id: "h3", x: 86, y: 8, w: 12, h: 8, label: "Submit ROPA", tooltip: "Submit once all 12 sections are filled." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Select department, then start with Organisation Details.", outcome: "Section fields load on the right; sidebar tracks completion." },
      { step: 2, hotspotId: "h2", action: "Fill required fields; use footer Next to advance.", outcome: "Section marked complete; Filled counter rises." },
      { step: 3, hotspotId: "h3", action: "Submit ROPA when all 12 sections are complete.", outcome: "Register locked for this department." },
    ],
    tips: ["Draft auto-saves. View Table shows existing submissions before you start."],
    warnings: [],
    expectedOutcome: "Processing activities documented for DPDP compliance.",
    nextHint: "Next: PIA and TPRM follow the same pattern.",
    livePath: "/user/ropa",
  },
  {
    id: "m-pia-1",
    chapterId: "modules",
    roles: ["user"],
    pageName: "PIA",
    purpose: "Privacy Impact Assessment for high-risk processing.",
    frame: "form-generic",
    frameLabel: "PIA",
    frameColor: "#818cf8",
    title: "PIA — Privacy Impact Assessment",
    description: "Assessment → PIA. Select department, then walk through 6 steps: General Info (incl. multi-select legal basis), Data Details, Data Flow, Risk Assessment (live score), Vendor & Controls, and Safeguards & Review.",
    hotspots: [
      { id: "h1", x: 2, y: 20, w: 19, h: 75, label: "6-Step Sidebar", tooltip: "General Info, Data Details, Data Flow, Risk Assessment, Vendor & Controls, Safeguards & Review." },
      { id: "h2", x: 23, y: 20, w: 75, h: 75, label: "Step Fields", tooltip: "Project context, legal basis cards, data flows, likelihood × impact risk score, vendor controls, and safeguards." },
      { id: "h3", x: 85, y: 8, w: 13, h: 8, label: "Save & Continue", tooltip: "Advances to next step; becomes Submit on the last step." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Select department, then work through the 6 steps in order.", outcome: "Each completed step is marked in the sidebar." },
      { step: 2, hotspotId: "h2", action: "Fill required fields; pick all applicable legal bases in General Info.", outcome: "Risk score updates live in the Risk Assessment step." },
      { step: 3, hotspotId: "h3", action: "Save & Continue after each step; Submit on Safeguards & Review.", outcome: "PIA finalised for department review." },
    ],
    tips: ["Attach evidence where fields allow. View Table lists submitted PIAs."],
    warnings: [],
    expectedOutcome: "High-risk processing formally assessed.",
    nextHint: "Next: TPRM for vendors.",
    livePath: "/user/pia",
  },
  {
    id: "m-tprm-1",
    chapterId: "modules",
    roles: ["user"],
    pageName: "TPRM",
    purpose: "Third-Party Risk Management — assess vendors handling personal data.",
    frame: "form-generic",
    frameLabel: "TPRM",
    frameColor: "#0ea5e9",
    title: "TPRM — Vendor Risk Assessment",
    description: "Assessment → TPRM. Select department, then use the risk score panel and 7-section sidebar on the left while answering vendor questions on the right.",
    hotspots: [
      { id: "h1", x: 2, y: 17, w: 22, h: 28, label: "Risk Score", tooltip: "Live overall score, risk band, answered/remaining counts, and completion %." },
      { id: "h2", x: 2, y: 46, w: 22, h: 49, label: "7 Sections", tooltip: "General Information, Cybersecurity & Data, Compliance, Financial Stability, Operational Risk, Legal, Reputation." },
      { id: "h3", x: 26, y: 17, w: 72, h: 70, label: "Questionnaire", tooltip: "Yes/No, select, and text questions with optional evidence uploads." },
      { id: "h4", x: 86, y: 88, w: 12, h: 9, label: "Next / Submit", tooltip: "Next Section validates required answers; Submit on the last section." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Select department, then start with General Information.", outcome: "Section questions load; sidebar score updates." },
      { step: 2, hotspotId: "h3", action: "Answer required questions; upload vendor evidence attachments.", outcome: "Answered count and risk score update live." },
      { step: 3, hotspotId: "h4", action: "Use Next Section through all 7 sections, then Submit.", outcome: "Vendor risk score finalised." },
    ],
    tips: ["Draft auto-saves. Review overall score in the sidebar before submitting.", "Run TPRM for every vendor before sharing personal data."],
    warnings: [],
    expectedOutcome: "Vendor risk documented and reviewable by admin.",
    nextHint: "Next: Breach Management.",
    livePath: "/user/tprm",
  },
  {
    id: "m-breach-1",
    chapterId: "modules",
    roles: ["user"],
    pageName: "Breach Management",
    purpose: "Report and track data breach incidents with DPDP notification workflows.",
    frame: "breach-management",
    title: "Breach Management — Track & Respond",
    description: "Breach Management shows active breaches, the DPDP 72-hour response timeline, and a searchable breach register.",
    hotspots: [
      { id: "h1", x: 83, y: 4, w: 15, h: 5, label: "Log New Breach", tooltip: "Opens the breach reporting workspace." },
      { id: "h2", x: 2, y: 8, w: 96, h: 6, label: "Active Breach Alert", tooltip: "Live countdown to the 72-hour DPB notification deadline." },
      { id: "h3", x: 2, y: 20, w: 96, h: 14, label: "DPDP Response Timeline", tooltip: "7-stage breach response — Detected → Escalation → DPO → DPB Notification → Data Principal Notice → Remediation → Closure." },
      { id: "h4", x: 2, y: 35, w: 96, h: 22, label: "Breach Register", tooltip: "All active breaches with severity, affected data principals, status, and DPB countdown clock." },
      { id: "h5", x: 2, y: 58, w: 47, h: 20, label: "Report New Breach", tooltip: "Launch the step-by-step breach reporting workspace." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Check the active breach banner and DPB countdown.", outcome: "You know how urgent the response is." },
      { step: 2, hotspotId: "h3", action: "Follow the DPDP Response Timeline to see the current stage.", outcome: "Understand what's done and what's next." },
      { step: 3, hotspotId: "h4", action: "Open a row in the Breach Register to view or update its status.", outcome: "Investigate, review, or mark a breach remediated." },
      { step: 4, hotspotId: "h5", action: "Click Launch Breach Reporting Workspace to log a new incident.", outcome: "A step-by-step form opens for the new breach." },
    ],
    tips: ["The DPB Clock counts down per breach — prioritise the ones closest to 72 hours.", "Report incidents within hours of detection — DPDP has strict notification timelines."],
    warnings: ["Incomplete severity/impact data delays regulatory response."],
    expectedOutcome: "Breach incident logged with notification tracking.",
    nextHint: "Next: Consent Variance.",
    livePath: "/user/breach-management",
  },
  {
    id: "m-consent-1",
    chapterId: "modules",
    roles: ["user"],
    pageName: "Consent Variance",
    purpose: "Compare consent records vs. DSPM data inventory to find mismatches.",
    frame: "consent-variance",
    title: "Consent Variance — Reconcile Data",
    description: "Identify data types collected without valid consent.",
    hotspots: [
      { id: "h1", x: 4, y: 14, w: 30, h: 10, label: "Department Selector", tooltip: "Pick department to compare." },
      { id: "h2", x: 4, y: 30, w: 92, h: 50, label: "Variance Table", tooltip: "Matched vs. unmatched data types." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Select a department from the dropdown.", outcome: "Department-specific comparison loads." },
      { step: 2, hotspotId: "h2", action: "Review red/mismatch rows requiring reconsent.", outcome: "Action list for marketing/legal teams." },
    ],
    tips: ["Run monthly after marketing or product changes."],
    warnings: ["Requires Consent and DSPM integrations enabled by Super Admin."],
    expectedOutcome: "Clear list of consent gaps to remediate.",
    nextHint: "Next: Policy Store.",
    livePath: "/user/consent-variance",
  },
  {
    id: "m-policy-1",
    chapterId: "modules",
    roles: ["user"],
    pageName: "Policy Store",
    purpose: "DPDP-aligned policy templates, uploads, AI generation, PDF export.",
    frame: "policy-store",
    title: "Policy Store — Manage Documents",
    description: "Policy Store lists every organisation policy — uploaded or AI-generated — tagged to the DPDP Act section it satisfies.",
    hotspots: [
      { id: "h1", x: 85, y: 8, w: 13, h: 9, label: "Upload / Generate", tooltip: "Upload your own policy or let AI draft one." },
      { id: "h2", x: 2, y: 25, w: 96, h: 73, label: "Organisation Policies", tooltip: "Uploaded and AI-generated policies — each tagged with its DPDP Act section, previewable and downloadable." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Click Upload Policy to add your own document.", outcome: "The policy is added to the library." },
      { step: 2, hotspotId: "h2", action: "Open a policy card to preview or download it as a PDF.", outcome: "A ready-to-share, DPDP-section-tagged document." },
    ],
    tips: ["Gap Assessment recommendations link directly to the missing policies here.", "AI-generated drafts cite the exact DPDP Act section (e.g. §9, §13)."],
    warnings: ["AI generation requires an AI Provider configured in Tenant Settings."],
    expectedOutcome: "Organisation policy library maintained and exportable.",
    nextHint: "Next: Reports.",
    livePath: "/user/policy-store",
  },
  {
    id: "m-reports-1",
    chapterId: "modules",
    roles: ["user"],
    pageName: "Reports",
    purpose: "Generate Executive, Summary, and Detailed compliance PDF reports.",
    frame: "reports",
    title: "Reports — Generate PDFs",
    description: "Reports gives Executive, Summary, and Detailed views with customisable charts, an organisation summary, and Key Risk Indicators.",
    hotspots: [
      { id: "h1", x: 3, y: 9, w: 94, h: 6, label: "Report Type Tabs", tooltip: "Switch between Executive, Summary, and Detailed report views." },
      { id: "h2", x: 88, y: 3, w: 10, h: 5, label: "Export PDF", tooltip: "Download the currently selected report as a PDF." },
      { id: "h3", x: 3, y: 16, w: 94, h: 30, label: "Visual Analytics", tooltip: "Customisable charts — module performance and DSAR status. Settings apply to the PDF export too." },
      { id: "h4", x: 3, y: 63, w: 94, h: 33, label: "Key Risk Indicators", tooltip: "Findings that need review, ranked by severity." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Choose Executive for leadership or Detailed for an audit trail.", outcome: "Report scope and depth changes." },
      { step: 2, hotspotId: "h3", action: "Customise the charts if needed, or leave the defaults.", outcome: "Chart settings carry over into the PDF export." },
      { step: 3, hotspotId: "h4", action: "Scroll to Key Risk Indicators for a prioritised action list.", outcome: "See exactly which findings need attention first." },
      { step: 4, hotspotId: "h2", action: "Click Export PDF once you're happy with the view.", outcome: "An audit-ready PDF is saved locally." },
    ],
    tips: ["Generate monthly Executive reports for leadership reviews.", "Chart color theme and toggles you set here are reflected in the exported PDF."],
    warnings: [],
    expectedOutcome: "Compliance report downloaded for stakeholders.",
    nextHint: "Next: Tenant Admin modules (if applicable).",
    livePath: "/user/reports",
  },

  // ── MODULES — TENANT ADMIN ─────────────────────────────────────────────
  {
    id: "m-users-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "User Management",
    purpose: "Create, edit, and deactivate organisation users.",
    frame: "tenant-users",
    title: "Manage Users — Create & Manage",
    description: "Tenant Admins → Users. Add users with email and password; edit, deactivate, or delete from the list.",
    hotspots: [
      { id: "h1", x: 87, y: 9, w: 11, h: 9, label: "Add User", tooltip: "Create a new user — name, email, password." },
      { id: "h2", x: 2, y: 19, w: 96, h: 13, label: "User Stats", tooltip: "Total, active, inactive users, and your license limit." },
      { id: "h3", x: 2, y: 42, w: 96, h: 47, label: "User List", tooltip: "Edit, deactivate, or delete any user." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Click Add User — enter name, email, password (8+ chars).", outcome: "User account created; welcome email sent if SMTP configured." },
      { step: 2, hotspotId: "h3", action: "Click Edit on a user to update email, password, or phone.", outcome: "User can log in with new credentials." },
      { step: 3, hotspotId: "h3", action: "Click Deactivate to block login without deleting the record.", outcome: "Inactive users cannot log in, but history is preserved." },
    ],
    tips: ["Create departments before users for scoped assessments."],
    warnings: ["License limit blocks new user creation — check the License page."],
    expectedOutcome: "Team members provisioned and manageable.",
    nextHint: "Next: Departments.",
    livePath: "/tenant/users",
  },
  {
    id: "m-dept-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "Departments",
    purpose: "Define organisational units for scoped assessments.",
    frame: "departments",
    title: "Departments — Org Structure",
    description: "Create departments with owners. Add one at a time, or bulk-add multiple at once.",
    hotspots: [
      { id: "h1", x: 78, y: 9, w: 20, h: 9, label: "Add Department", tooltip: "Add one department, or bulk-add via Add Multiple." },
      { id: "h2", x: 2, y: 21, w: 96, h: 13, label: "Department Stats", tooltip: "Total, active, inactive, and unique department owners." },
      { id: "h3", x: 2, y: 44, w: 96, h: 54, label: "Department Table", tooltip: "Name, owner, email, status, and Edit/Deactivate actions." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Click Add One for a single department, or Add Multiple for bulk entry.", outcome: "Departments become available for user assignment." },
      { step: 2, hotspotId: "h3", action: "Verify each department has an owner assigned.", outcome: "Clean org structure for reporting and assessments." },
    ],
    tips: ["Match department names to your HR org chart."],
    warnings: [],
    expectedOutcome: "Departments ready for gap assessments and variance reports.",
    nextHint: "Next: Form Questions.",
    livePath: "/tenant/departments",
  },
  {
    id: "m-gapadmin-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "Gap Assessment Admin",
    purpose: "Manage cycles, scores, gaps, and evidence.",
    frame: "gap-admin",
    title: "Gap Admin — Assessment Cycles",
    description: "Open cycles, monitor department scores, and review gaps across nine tabs — Overview, Departments, Users, Compliance, Gaps, Recommendations, Evidence, History, and Cycles.",
    hotspots: [
      { id: "h1", x: 68, y: 8, w: 30, h: 10, label: "Cycle Selector", tooltip: "Switch assessment cycle, or start a New Cycle." },
      { id: "h2", x: 2, y: 22, w: 96, h: 8, label: "Tab Bar", tooltip: "Overview, Departments, Users, Compliance, Gaps, Recommendations, Evidence, History, Cycles." },
      { id: "h3", x: 2, y: 36, w: 96, h: 15, label: "Assessment Tracking Stats", tooltip: "Total, draft, submitted, locked, and reopened assessments, plus overall completion %." },
      { id: "h4", x: 2, y: 53, w: 96, h: 45, label: "Status Charts", tooltip: "Department Status and Assessment Status breakdowns." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Click New Cycle and set start/end dates.", outcome: "Users can submit during the open window." },
      { step: 2, hotspotId: "h3", action: "Check Assessment Tracking for Draft vs. Submitted vs. Locked counts.", outcome: "Know exactly how many departments still need to respond." },
      { step: 3, hotspotId: "h2", action: "Switch to the Gaps tab for remediation tracking.", outcome: "Gap closure tracked over time." },
    ],
    tips: ["Close cycles on schedule to lock submissions for that period.", "The Users tab shows individual submission status, not just department rollups."],
    warnings: ["Users cannot submit without an open cycle."],
    expectedOutcome: "Assessment programme managed end-to-end.",
    nextHint: "Next: review submitted responses in detail.",
    livePath: "/tenant/gap-assessment",
  },
  {
    id: "m-responses-gap-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "User Form Responses — Gap Assessment",
    purpose: "See every Gap Assessment answer submitted by users, department by department.",
    frame: "user-responses",
    frameLabel: "Gap Assessment",
    title: "User Form Responses — Gap Assessment",
    description: "Monitor every answer submitted across Gap Assessment, ROPA, PIA, and TPRM. Click View on any row for the full question-and-answer detail.",
    hotspots: [
      { id: "h1", x: 2, y: 27, w: 96, h: 8, label: "Form Tabs", tooltip: "Switch between Gap Assessment, ROPA, PIA, and TPRM." },
      { id: "h2", x: 2, y: 47, w: 96, h: 12, label: "Submission Stats", tooltip: "Total submissions, average score, compliant count, and critical count." },
      { id: "h3", x: 2, y: 62, w: 96, h: 36, label: "Responses Table", tooltip: "Department, submitter, score, status, and answered count — click View for full detail." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Check the Critical stat for departments scoring below 50%.", outcome: "Immediately spot who needs the most support." },
      { step: 2, hotspotId: "h3", action: "Click View on a row to see every question and answer.", outcome: "Full audit trail of what was submitted and when." },
    ],
    tips: ["Use the department filter to focus on one team at a time."],
    warnings: [],
    expectedOutcome: "You can audit exactly what every department answered.",
    nextHint: "Next: License usage.",
    livePath: "/tenant/responses/gap_assessment",
  },
  {
    id: "m-responses-ropa-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "User Form Responses — ROPA",
    purpose: "See every ROPA submission and its section completion.",
    frame: "user-responses",
    frameLabel: "ROPA",
    title: "User Form Responses — ROPA",
    description: "Records of Processing Activities submitted by users, with per-submission section progress.",
    hotspots: [
      { id: "h1", x: 2, y: 25, w: 96, h: 8, label: "Form Tabs", tooltip: "Switch between Gap Assessment, ROPA, PIA, and TPRM." },
      { id: "h2", x: 2, y: 44, w: 96, h: 12, label: "ROPA Stats", tooltip: "Submissions, fully complete count, in-progress count, and average progress %." },
      { id: "h3", x: 2, y: 58, w: 96, h: 34, label: "ROPA Table", tooltip: "Department, submitter, progress %, and sections filled — click View for full detail." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Check Avg Progress to see how far along ROPA is org-wide.", outcome: "Understand overall documentation coverage." },
      { step: 2, hotspotId: "h3", action: "Click View on an in-progress row.", outcome: "See exactly which of the 12 sections are still empty." },
    ],
    tips: ["A ROPA stuck below 100% usually means a required field was left blank in one section."],
    warnings: [],
    expectedOutcome: "You can track ROPA completion across the organisation.",
    nextHint: "Next: PIA responses.",
    livePath: "/tenant/responses/ropa",
  },
  {
    id: "m-responses-pia-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "User Form Responses — PIA",
    purpose: "See every PIA submission and its risk score.",
    frame: "user-responses",
    frameLabel: "PIA",
    title: "User Form Responses — PIA",
    description: "Privacy Impact Assessment responses and risk scores, submitted per project or vendor.",
    hotspots: [
      { id: "h1", x: 2, y: 25, w: 96, h: 8, label: "Form Tabs", tooltip: "Switch between Gap Assessment, ROPA, PIA, and TPRM." },
      { id: "h2", x: 2, y: 44, w: 96, h: 12, label: "PIA Stats", tooltip: "Submissions, high/critical risk count, sensitive-data count, and average risk score." },
      { id: "h3", x: 2, y: 58, w: 96, h: 34, label: "PIA Table", tooltip: "Project, department, risk score, and risk level — click View for full detail." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Check High / Critical count first.", outcome: "Immediately see which projects carry elevated privacy risk." },
      { step: 2, hotspotId: "h3", action: "Click View on a High or Critical row.", outcome: "Read the full risk narrative and mitigation notes." },
    ],
    tips: ["Risk Score = Likelihood × Impact — cross-check against your organisation's risk appetite."],
    warnings: [],
    expectedOutcome: "You can prioritise which high-risk projects need follow-up.",
    nextHint: "Next: License usage.",
    livePath: "/tenant/responses/pia",
  },
  {
    id: "m-forms-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "Form Questions",
    purpose: "Customise Gap Assessment, ROPA, PIA, and TPRM questionnaires.",
    frame: "form-questions",
    title: "Form Questions — Customise Assessments",
    description: "Import default questions, bulk upload via CSV, or add questions one at a time — per form type.",
    hotspots: [
      { id: "h1", x: 62, y: 8, w: 36, h: 10, label: "Question Actions", tooltip: "Import Defaults, Bulk Upload, or Add Question." },
      { id: "h2", x: 2, y: 22, w: 96, h: 8, label: "Form Tabs", tooltip: "Switch between Gap Assessment, ROPA, PIA, and TPRM question sets." },
      { id: "h3", x: 2, y: 33, w: 96, h: 15, label: "Question Stats", tooltip: "Total, active, inactive, required questions, and section count." },
      { id: "h4", x: 2, y: 58, w: 96, h: 40, label: "Questions Table", tooltip: "Edit or deactivate any question." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Select a form type tab.", outcome: "Questions for that form are displayed." },
      { step: 2, hotspotId: "h1", action: "Add a question with section, type, and required flag — or bulk upload a CSV.", outcome: "Question appears on the user's form immediately." },
      { step: 3, hotspotId: "h4", action: "Deactivate outdated questions instead of deleting them.", outcome: "Historical submissions remain valid and comparable." },
    ],
    tips: ["Use Import Defaults to start from the built-in DPDP question bank, then customise."],
    warnings: [],
    expectedOutcome: "Forms tailored to your organisation's needs.",
    nextHint: "Next: monitor what users have submitted.",
    livePath: "/tenant/form-questions/gap_assessment",
  },

  // ── MODULES — ADMIN ────────────────────────────────────────────────────
  {
    id: "m-tenants-1",
    chapterId: "modules",
    roles: ["admin"],
    pageName: "Tenant Management",
    purpose: "Create and manage tenant organisations.",
    frame: "admin-tenants",
    title: "Tenants — Onboard Organisations",
    description: "Create tenant with admin credentials, license, and modules.",
    hotspots: [
      { id: "h1", x: 68, y: 10, w: 28, h: 8, label: "Add Tenant", tooltip: "Create organisation." },
      { id: "h2", x: 4, y: 24, w: 92, h: 55, label: "Tenant Table", tooltip: "Edit, suspend tenants." },
      { id: "h3", x: 4, y: 82, w: 40, h: 12, label: "Module Toggles", tooltip: "Consent, DSAR, DSPM, DPIA." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Click Add Tenant — company name, admin email/password.", outcome: "Tenant DB provisioned automatically." },
      { step: 2, hotspotId: "h3", action: "Enable integration modules for the tenant.", outcome: "Dashboard stats populate for those modules." },
      { step: 3, hotspotId: "h2", action: "Share the login link and admin credentials with the customer.", outcome: "Customer can log in and set up org." },
    ],
    tips: ["Use a clear organisation name — customers see it in the app and emails."],
    warnings: ["Organisation name can be updated later from Edit Tenant."],
    expectedOutcome: "New customer organisation live on platform.",
    nextHint: "Next: platform settings.",
    livePath: "/admin/tenants",
  },
  {
    id: "m-settings-admin-1",
    chapterId: "modules",
    roles: ["admin"],
    pageName: "Platform Settings",
    purpose: "Platform-wide SMTP and configuration.",
    frame: "settings",
    title: "Settings — Platform Configuration",
    description: "Configure platform-level SMTP used as the fallback for any tenant that hasn't set up its own.",
    hotspots: [
      { id: "h1", x: 4, y: 14, w: 45, h: 40, label: "SMTP Panel", tooltip: "Host, port, credentials." },
      { id: "h2", x: 52, y: 14, w: 44, h: 40, label: "Email Templates", tooltip: "Default header/footer branding." },
      { id: "h3", x: 4, y: 60, w: 92, h: 30, label: "Email Logs", tooltip: "Delivery success/failure, platform-wide." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Enter platform SMTP host, port, username, password. Enable TLS.", outcome: "Tenants without their own SMTP can still send email." },
      { step: 2, hotspotId: "h1", action: "Send a test email to verify.", outcome: "Confirmation in Email Logs." },
    ],
    tips: ["Configure platform SMTP before onboarding tenants."],
    warnings: ["This is the fallback for ALL tenants — misconfiguring it affects every organisation without its own SMTP."],
    expectedOutcome: "Reliable platform-wide email delivery.",
    nextHint: "Next: end-to-end journey.",
    livePath: "/admin/settings",
  },
  {
    id: "m-settings-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "Settings",
    purpose: "SMTP, AI provider, and breach notification configuration.",
    frame: "settings-tenant",
    title: "Settings — Organisation Configuration",
    description: "Your profile and organisation name, Organization SMTP, Email Template Branding, Email Activities Logs, AI Provider Configuration, and Breach Notification Settings — all on one page.",
    hotspots: [
      { id: "h1", x: 2, y: 6, w: 96, h: 11, label: "Profile & Tenant Info", tooltip: "Your name, email, role, and organisation name." },
      { id: "h2", x: 2, y: 18, w: 96, h: 20, label: "Organization SMTP", tooltip: "Configure your org's mail server for outgoing notifications." },
      { id: "h3", x: 2, y: 47, w: 96, h: 10, label: "AI Provider Configuration", tooltip: "Connect OpenAI, Anthropic, Groq, Azure, or Ollama for AI-generated policies." },
      { id: "h4", x: 2, y: 58, w: 96, h: 7, label: "Breach Notification Settings", tooltip: "Set how often reminder emails go out for unresolved breaches." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Enter SMTP host, port, username, password, and enable TLS.", outcome: "Your organisation can send its own branded emails." },
      { step: 2, hotspotId: "h2", action: "Send a test email to verify delivery.", outcome: "Confirmation appears in Email Activities Logs." },
      { step: 3, hotspotId: "h3", action: "Add an AI provider (e.g. Azure OpenAI) with your API key.", outcome: "Unlocks AI Generate in Policy Store." },
      { step: 4, hotspotId: "h4", action: "Set the Breach Alert Reminder Interval.", outcome: "Reporters get reminded until the incident is resolved." },
    ],
    tips: ["Configure SMTP before creating users so welcome emails work.", "AI generation requires an active provider here — otherwise Policy Store's AI Generate stays disabled."],
    warnings: ["Wrong SMTP credentials show as failed in Email Activities Logs."],
    expectedOutcome: "Reliable email delivery and AI features enabled for the organisation.",
    nextHint: "Next: end-to-end journey.",
    livePath: "/tenant/settings",
  },
  {
    id: "m-license-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "License & Usage",
    purpose: "Track your plan, user slots, and expiry date.",
    frame: "license",
    title: "License & Usage",
    description: "Subscription details and usage tracking — plan tier, users created vs. limit, and expiry.",
    hotspots: [
      { id: "h1", x: 2, y: 17, w: 96, h: 12, label: "License Summary", tooltip: "Plan tier, users created, utilisation %, and slots left." },
      { id: "h2", x: 2, y: 31, w: 96, h: 32, label: "License Details", tooltip: "Full breakdown — plan, user limit, users created, slots left, expiry date, and status." },
      { id: "h3", x: 2, y: 65, w: 96, h: 15, label: "Usage Bar", tooltip: "Visual progress toward your user limit." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Check Slots Left before onboarding a batch of new users.", outcome: "Avoid hitting the limit mid-onboarding." },
      { step: 2, hotspotId: "h2", action: "Note the Expires date.", outcome: "Renew before the license lapses and blocks access." },
    ],
    tips: ["Contact your OneDPDP account manager to increase the plan limit before you hit it."],
    warnings: ["Once slots run out, new user creation fails with a 403 error until you free up or add slots."],
    expectedOutcome: "You know exactly how much room your organisation has to grow.",
    nextHint: "Next: brand your outgoing emails.",
    livePath: "/tenant/license",
  },
  {
    id: "m-emailtpl-1",
    chapterId: "modules",
    roles: ["tenant_admin"],
    pageName: "Email Templates",
    purpose: "Brand outgoing notification emails with your organisation's look.",
    frame: "email-templates",
    title: "Email Templates — Brand Your Notifications",
    description: "Customise the header, footer, and color scheme of every system email — with a live preview as you edit.",
    hotspots: [
      { id: "h1", x: 68, y: 3, w: 30, h: 6, label: "Template Actions", tooltip: "Load Preset, Reset to platform default, or Save Template." },
      { id: "h2", x: 2, y: 14, w: 47, h: 58, label: "Branding & Colors", tooltip: "Pick a color preset, set your logo text, and customise footer text." },
      { id: "h3", x: 52, y: 14, w: 46, h: 84, label: "Live Preview", tooltip: "See exactly how your branded emails will look, updated as you type." },
      { id: "h4", x: 2, y: 73, w: 47, h: 14, label: "Send Test Email", tooltip: "Send a real test email to confirm rendering and SMTP delivery." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Pick a color preset and enter your organisation name.", outcome: "Live Preview updates instantly on the right." },
      { step: 2, hotspotId: "h4", action: "Send a test email to yourself.", outcome: "Confirms both the branding and your SMTP settings work end-to-end." },
      { step: 3, hotspotId: "h1", action: "Click Save Template once you're happy.", outcome: "All future notification emails use this branding." },
    ],
    tips: ["Configure SMTP in Settings first — Send Test Email needs it to actually deliver."],
    warnings: ["Reset (Fallback to Platform) discards your custom branding immediately."],
    expectedOutcome: "Your organisation's emails look consistent and on-brand.",
    nextHint: "Next: the rest of Settings.",
    livePath: "/tenant/email-templates",
  },

  // ── JOURNEY ────────────────────────────────────────────────────────────
  {
    id: "j1",
    chapterId: "journey",
    roles: ["all"],
    pageName: "Complete Workflow",
    purpose: "See the full path from first login to full compliance.",
    frame: "journey",
    title: "End-to-End Compliance Journey",
    description: "Seven stages from login to Compliant — Tenant Admin sets things up, Users work through each module, Admin oversees the platform throughout.",
    hotspots: [
      { id: "h1", x: 4, y: 44, w: 9, h: 26, label: "Login", tooltip: "Tenant Admin sets up the assessment cycle and users." },
      { id: "h2", x: 18, y: 44, w: 9, h: 26, label: "Gap Assessment", tooltip: "User answers section-by-section questions to baseline maturity." },
      { id: "h3", x: 32, y: 44, w: 9, h: 26, label: "PIA", tooltip: "Privacy risk assessed for high-risk processing." },
      { id: "h4", x: 46, y: 44, w: 9, h: 26, label: "ROPA", tooltip: "Processing activities documented per department." },
      { id: "h5", x: 59, y: 44, w: 9, h: 26, label: "TPRM", tooltip: "Vendors and third parties risk-assessed." },
      { id: "h6", x: 73, y: 44, w: 9, h: 26, label: "Breach Readiness", tooltip: "72-hour DPB notification workflow in place." },
      { id: "h7", x: 87, y: 44, w: 9, h: 26, label: "Compliant", tooltip: "Target: 13 May 2027." },
      { id: "h8", x: 4, y: 86, w: 92, h: 11, label: "Role Legend", tooltip: "Who does what across the journey." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Tenant Admin logs in and opens the assessment cycle for the org.", outcome: "Users can now access their modules." },
      { step: 2, hotspotId: "h2", action: "User completes Gap Assessment first.", outcome: "Baseline maturity and gaps identified." },
      { step: 3, hotspotId: "h3", action: "User completes PIA for high-risk processing.", outcome: "Privacy risks and mitigations documented." },
      { step: 4, hotspotId: "h4", action: "User documents ROPA for each processing activity.", outcome: "Full processing record for auditors." },
      { step: 5, hotspotId: "h5", action: "User assesses vendors via TPRM.", outcome: "Third-party risk on record." },
      { step: 6, hotspotId: "h6", action: "User sets up breach response readiness.", outcome: "72-hour DPB notification workflow ready if needed." },
      { step: 7, hotspotId: "h7", action: "Track overall % on Home until you reach Compliant.", outcome: "Audit-ready compliance by 13 May 2027." },
      { step: 8, hotspotId: "h8", action: "Remember each role's part — Tenant Admin sets up, User executes, Admin oversees.", outcome: "Everyone knows their responsibility in the journey." },
    ],
    tips: ["Don't skip Gap Assessment — it drives recommendations everywhere else.", "Track progress for the whole journey from the Home page Road to Compliance timeline."],
    warnings: [],
    expectedOutcome: "Full picture of how all modules connect in sequence, and who owns each step.",
    nextHint: "Next: common tasks.",
    livePath: null,
  },

  // ── COMMON TASKS ─────────────────────────────────────────────────────
  {
    id: "t1",
    chapterId: "tasks",
    roles: ["user"],
    pageName: "Task: First Login",
    purpose: "Log in and reach your home dashboard.",
    frame: "login",
    frameLabel: "User",
    title: "How to Log In",
    description: "Use your organisation's login link, then your email and password.",
    hotspots: [
      { id: "h1", x: 61, y: 33, w: 28, h: 8, label: "Email", tooltip: "Your registered work email." },
      { id: "h2", x: 61, y: 44, w: 28, h: 8, label: "Password", tooltip: "Your case-sensitive password." },
      { id: "h3", x: 61, y: 55, w: 28, h: 8, label: "Captcha", tooltip: "Complete the 'I'm not a robot' check." },
      { id: "h4", x: 61, y: 65, w: 28, h: 8, label: "Sign In", tooltip: "Submit to enter your workspace." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Enter your email address.", outcome: "Email validated." },
      { step: 2, hotspotId: "h2", action: "Enter your password.", outcome: "Credentials ready." },
      { step: 3, hotspotId: "h3", action: "Complete the captcha check.", outcome: "Bot protection cleared." },
      { step: 4, hotspotId: "h4", action: "Click Sign In.", outcome: "Redirected to your Home page." },
    ],
    tips: ["Use the login link your Tenant Admin shared — it already points to your organisation."],
    warnings: ["Wrong org link = 'Invalid credentials' even with the correct password."],
    expectedOutcome: "Successfully logged in to OneDPDP.",
    nextHint: "Next: FAQ.",
    livePath: "/login",
  },
  {
    id: "t2",
    chapterId: "tasks",
    roles: ["tenant_admin"],
    pageName: "Task: Onboard New User",
    purpose: "Add a team member in under 2 minutes.",
    frame: "tenant-users",
    title: "Quick Task: Add a User",
    description: "Tenant Admins → Users → Add User → share credentials.",
    hotspots: [
      { id: "h1", x: 87, y: 9, w: 11, h: 9, label: "Add User", tooltip: "Start here." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Click Add User.", outcome: "Modal opens." },
      { step: 2, hotspotId: "h1", action: "Fill name, email, password → Create.", outcome: "User can log in immediately." },
    ],
    tips: ["Tell the user their organisation login link when sharing credentials."],
    warnings: [],
    expectedOutcome: "New user ready to work.",
    nextHint: "Next: FAQ.",
    livePath: "/tenant/users",
  },
  {
    id: "t3",
    chapterId: "tasks",
    roles: ["user"],
    pageName: "Task: Submit Assessment",
    purpose: "Complete and submit any assessment form.",
    frame: "gap-assessment-submit",
    title: "Quick Task: Submit Any Form",
    description: "Same pattern for Gap, ROPA, PIA, TPRM — pick a section on the left, answer on the right, then Save Draft or Submit below the fold.",
    hotspots: [
      { id: "h1", x: 2, y: 25, w: 19, h: 70, label: "Section Sidebar", tooltip: "Switch sections — your score updates as you complete each one." },
      { id: "h2", x: 23, y: 42, w: 75, h: 52, label: "Answer & Comment", tooltip: "Answer the question, then add comments or evidence. Save Draft / Submit sit at the bottom of the page." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h1", action: "Pick a section from the sidebar.", outcome: "That section's questions load on the right." },
      { step: 2, hotspotId: "h2", action: "Answer, add comments, then scroll down to Save Draft after each section.", outcome: "No lost progress." },
      { step: 3, hotspotId: "h2", action: "Submit when every section is 100% complete.", outcome: "Submission appears in history and reports." },
    ],
    tips: ["View submissions via Assessment → Submissions link."],
    warnings: [],
    expectedOutcome: "Form submitted successfully.",
    nextHint: "Next: FAQ.",
    livePath: "/user/gap-assessment",
  },

  // ── FAQ ──────────────────────────────────────────────────────────────
  {
    id: "f1",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Login & Access",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "Invalid Credentials?",
    description: "Most login failures come down to three things: the wrong organisation link, an inactive account, or a password typo — not a broken system.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 4, y: 31, w: 29.5, h: 29.4, label: "Login &amp; Access", tooltip: "Everything filed under Login &amp; Access." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Confirm you're on YOUR organisation's login link, not a colleague's or a demo link.", outcome: "The right tenant database gets targeted." },
      { step: 2, hotspotId: "h2", action: "Ask your Tenant Admin to confirm your account is Active on the Users page.", outcome: "Inactive accounts are blocked at login even with the correct password." },
      { step: 3, hotspotId: "h2", action: "Re-type your password rather than pasting it — a trailing space is a common cause.", outcome: "Login succeeds." },
    ],
    tips: ["Use the organisation login link your admin shared — then enter only your email and password."],
    warnings: [],
    expectedOutcome: "Login issue diagnosed and resolved.",
    nextHint: "Next: missing menu items.",
    livePath: "/help",
  },
  {
    id: "f2",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Navigation",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "Why Don't I See a Menu Item?",
    description: "Your navigation bar is role-based on purpose — User, Tenant Admin, and Admin each see a different, smaller set of items.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 4, y: 31, w: 29.5, h: 29.4, label: "Login &amp; Access", tooltip: "Everything filed under Login &amp; Access." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Check which role you're logged in as via the profile badge, top-right.", outcome: "Confirms whether the missing item is even meant for your role." },
      { step: 2, hotspotId: "h2", action: "Users see Home, Dashboard, Assessment, Breach Management, Consent Variance, DSAR, Policy Store, Reports, Help.", outcome: "That's the complete User menu — nothing else is hidden." },
      { step: 3, hotspotId: "h2", action: "Tenant Admins see Dashboard, Tenant Admins (Users/Departments/Gap Admin/Form Questions), License, Settings.", outcome: "That's the complete Tenant Admin menu." },
    ],
    tips: ["If an entire module like DSAR is missing, it may not be enabled for your organisation — ask your Tenant Admin, not IT."],
    warnings: [],
    expectedOutcome: "You know whether a missing item is a permissions issue or a real bug.",
    nextHint: "Next: submission problems.",
    livePath: "/help",
  },
  {
    id: "f3",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Submissions",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "Why Can't I Submit My Assessment?",
    description: "Gap Assessment, ROPA, PIA, and TPRM all require an open assessment cycle before Submit becomes available — this applies to every user, not just you.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 35.25, y: 31, w: 29.5, h: 29.4, label: "Assessments", tooltip: "Everything filed under Assessments." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Ask your Tenant Admin to check Gap Admin — the cycle selector should read '(Current)'.", outcome: "Confirms whether a cycle is actually open." },
      { step: 2, hotspotId: "h2", action: "Tenant Admin clicks New Cycle with a start/end date if none is open.", outcome: "Submit unlocks for every user in the organisation." },
      { step: 3, hotspotId: "h2", action: "Retry Submit once the cycle is confirmed open.", outcome: "The assessment locks and appears in Submissions." },
    ],
    tips: [],
    warnings: ["Without an open cycle, Submit stays disabled for every single user — this is not a per-account bug."],
    expectedOutcome: "You know exactly who needs to act to unblock submission.",
    nextHint: "Next: submissions stuck below 100%.",
    livePath: "/help",
  },
  {
    id: "f4",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Submissions",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "Why Is My Submission Stuck Below 100%?",
    description: "A ROPA, PIA, or TPRM sitting below 100% almost always means one required field in a specific section was left blank — not a saving bug.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 35.25, y: 31, w: 29.5, h: 29.4, label: "Assessments", tooltip: "Everything filed under Assessments." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Check the sections count on your form, e.g. 10 / 12 sections filled.", outcome: "Identifies exactly how many sections are incomplete." },
      { step: 2, hotspotId: "h2", action: "Open each incomplete section and look for a field with no value.", outcome: "The missing field is found." },
      { step: 3, hotspotId: "h2", action: "Fill it and save.", outcome: "Section count and progress % update immediately." },
    ],
    tips: ["Required fields aren't always visually flagged — if a section refuses to tick complete, that's your signal to check it."],
    warnings: [],
    expectedOutcome: "Submission reaches 100% and can be finalised.",
    nextHint: "Next: editing after submission.",
    livePath: "/help",
  },
  {
    id: "f5",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Submissions",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "Can I Edit After Submitting?",
    description: "Submitted assessments lock automatically. A Tenant Admin can reopen one from Gap Admin if a genuine correction is needed.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 35.25, y: 31, w: 29.5, h: 29.4, label: "Assessments", tooltip: "Everything filed under Assessments." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "User: contact your Tenant Admin — you can't self-service a reopen.", outcome: "The request is routed to the person who can act." },
      { step: 2, hotspotId: "h2", action: "Tenant Admin: Gap Admin → find the submission → Reopen.", outcome: "Status moves from Locked to Reopened; the user can edit again." },
      { step: 3, hotspotId: "h2", action: "User resubmits once corrections are made.", outcome: "Updated answers are recorded, with history preserved." },
    ],
    tips: ["The Reopened counter on Gap Admin's Overview tab shows how many submissions are currently unlocked for editing."],
    warnings: [],
    expectedOutcome: "Correction made without losing the audit trail.",
    nextHint: "Next: adding new users.",
    livePath: "/help",
  },
  {
    id: "f6",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Licenses",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "Why Can't I Add a New User?",
    description: "User creation fails with a 403 error once you've used every slot on your plan.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 4, y: 62.4, w: 29.5, h: 29.4, label: "Users &amp; Licenses", tooltip: "Everything filed under Users &amp; Licenses." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Tenant Admin: open License & Usage and check Slots Left.", outcome: "Confirms whether you're actually at the limit." },
      { step: 2, hotspotId: "h2", action: "Deactivate any unused accounts to free a slot, or contact your account manager to raise the plan limit.", outcome: "Slots become available again." },
      { step: 3, hotspotId: "h2", action: "Retry Add User.", outcome: "Account created successfully." },
    ],
    tips: [],
    warnings: ["Deactivating a user frees a slot; Deleting also removes their submission history — deactivate first unless you're certain."],
    expectedOutcome: "New team member onboarded without losing anyone's history.",
    nextHint: "Next: missing emails.",
    livePath: "/help",
  },
  {
    id: "f7",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Email",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "I Didn't Receive an Email",
    description: "Welcome emails, task assignments, and breach alerts all depend on SMTP being correctly configured and enabled — not just filled in.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 35.25, y: 62.4, w: 29.5, h: 29.4, label: "Email &amp; Notifications", tooltip: "Everything filed under Email &amp; Notifications." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Tenant Admin: Settings → Organization SMTP → confirm 'Enable SMTP for outgoing email' is checked.", outcome: "Confirms SMTP is actually turned on, not just filled in." },
      { step: 2, hotspotId: "h2", action: "Click Send Test to yourself.", outcome: "Confirms host, port, and credentials work end-to-end." },
      { step: 3, hotspotId: "h2", action: "If the test doesn't arrive, check Email Activities Logs for the failure reason.", outcome: "Pinpoints a credentials, spam-filter, or firewall issue." },
    ],
    tips: ["When your org SMTP is disabled, OneDPDP quietly falls back to platform SMTP — emails may still send, just not from your domain."],
    warnings: [],
    expectedOutcome: "Notification emails deliver reliably.",
    nextHint: "Next: the breach notification clock.",
    livePath: "/help",
  },
  {
    id: "f8",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Breach",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "What Happens at the 72-Hour Mark?",
    description: "Under DPDP Act 2023, a Data Fiduciary must notify the Data Protection Board within the prescribed period — OneDPDP tracks this as a 72-hour countdown per breach.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 66.5, y: 31, w: 29.5, h: 29.4, label: "Breach &amp; Compliance", tooltip: "Everything filed under Breach &amp; Compliance." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Open Breach Management and check the DPB Clock column for each active breach.", outcome: "See exactly how much time is left, per incident." },
      { step: 2, hotspotId: "h2", action: "Work through the Notification Checklist — Breach contained → Impact assessment → DPO notified → DPB notification drafted.", outcome: "A structured, auditable response." },
      { step: 3, hotspotId: "h2", action: "File the DPB notification before the clock hits zero.", outcome: "Avoids exposure to penalties of up to ₹250 Crore." },
    ],
    tips: [],
    warnings: ["Missing the 72-hour window is a compliance failure regardless of whether the breach itself was contained."],
    expectedOutcome: "You understand the regulatory stakes behind the countdown.",
    nextHint: "Next: AI Generate greyed out.",
    livePath: "/help",
  },
  {
    id: "f9",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — AI & Policies",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "Why Is AI Generate Greyed Out?",
    description: "Policy Store's AI-drafting feature needs an active AI provider connected in Settings first.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 66.5, y: 62.4, w: 29.5, h: 29.4, label: "Policies &amp; AI", tooltip: "Everything filed under Policies &amp; AI." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Tenant Admin: Settings → AI Provider Configuration → Add provider.", outcome: "Connect OpenAI, Anthropic, Groq, Azure, or Ollama with an API key." },
      { step: 2, hotspotId: "h2", action: "Click Test next to the provider to confirm the key works.", outcome: "The provider shows ACTIVE." },
      { step: 3, hotspotId: "h2", action: "Return to Policy Store — Generate Policy is now enabled.", outcome: "AI drafts save to Policy Store as PDFs, cited to the exact DPDP Act section." },
    ],
    tips: ["API keys are encrypted and only ever shown partially masked (e.g. 5NB****wSCw) — you can't view the full key again after saving."],
    warnings: [],
    expectedOutcome: "AI policy generation works end-to-end.",
    nextHint: "Next: zeroed-out Dashboard stats.",
    livePath: "/help",
  },
  {
    id: "f10",
    chapterId: "faq",
    roles: ["all"],
    pageName: "FAQ — Dashboard",
    purpose: "Quick answers and fixes for common problems.",
    frame: "faq",
    title: "Why Are My Dashboard Stats Showing 0%?",
    description: "Consent, Cookie, and DSAR tiles pull live numbers from connected integrations — a 0 usually means that integration isn't connected yet, not that the feature is broken.",
    hotspots: [
      { id: "h1", x: 4, y: 18, w: 92, h: 8, label: "Search", tooltip: "Type your problem in your own words." },
      { id: "h2", x: 35.25, y: 31, w: 29.5, h: 29.4, label: "Assessments", tooltip: "Everything filed under Assessments." },
    ],
    clickSequence: [
      { step: 1, hotspotId: "h2", action: "Check Last Refreshed at the top of the Dashboard.", outcome: "Rules out simply stale, cached data." },
      { step: 2, hotspotId: "h2", action: "Click Refresh to force a reload from the Consent, DSAR, and DSPM APIs.", outcome: "Confirms whether the numbers were just uncached." },
      { step: 3, hotspotId: "h2", action: "If still 0, ask your Tenant Admin whether that integration is enabled for your organisation.", outcome: "Correctly diagnoses 'not connected' vs. 'broken'." },
    ],
    tips: ["ROPA, Gap Assessment, and DPIA tiles come from your own submissions, not external integrations — those show 0 until someone actually fills a form."],
    warnings: [],
    expectedOutcome: "Zeroed stats correctly diagnosed as configuration, not a bug.",
    nextHint: "Tour complete! Try it live or restart the tour.",
    livePath: "/help",
  },
];

/** Interactive product tour step → license module (via livePath or step id). */
const TOUR_LIVE_PATH_MODULE = {
  "/user/gap-assessment": "gap",
  "/user/gap-assessment/submissions": "gap",
  "/user/ropa": "ropa",
  "/user/pia": "dpia",
  "/user/tprm": "tprm",
  "/user/breach-management": "breach",
  "/user/consent-variance": "consent",
  "/user/policy-store": "policy_store",
  "/user/reports": "reports",
  "/user/asset-tracker": "asset_tracker",
  "/tenant/gap-assessment": "gap",
  "/tenant/responses/gap_assessment": "gap",
  "/tenant/responses/ropa": "ropa",
  "/tenant/responses/pia": "dpia",
  "/tenant/form-questions/gap_assessment": "gap",
};

const TOUR_STEP_ID_MODULE = {
  f8: "breach",
  f9: "policy_store",
};

function tourStepModuleAllowed(step, allowedModules, role) {
  if (role === "admin") return true;
  const required =
    step.requiredModule ||
    (step.livePath ? TOUR_LIVE_PATH_MODULE[step.livePath] : null) ||
    TOUR_STEP_ID_MODULE[step.id];
  if (!required) return true;
  const keys = Array.isArray(required) ? required : [required];
  return keys.some((key) => (allowedModules || []).includes(key));
}

export function getStepsForRole(role, chapterId = null, allowedModules = null) {
  return TOUR_STEPS.filter((s) => {
    const roleOk = s.roles.includes("all") || s.roles.includes(role);
    const chapterOk = !chapterId || s.chapterId === chapterId;
    const moduleOk =
      allowedModules == null || tourStepModuleAllowed(s, allowedModules, role);
    return roleOk && chapterOk && moduleOk;
  });
}

export function getChaptersForRole(role, allowedModules = null) {
  const steps = getStepsForRole(role, null, allowedModules);
  const chapterIds = new Set(steps.map((s) => s.chapterId));
  return TOUR_CHAPTERS.filter((c) => chapterIds.has(c.id)).sort((a, b) => a.order - b.order);
}

export function getTotalStepsForRole(role, allowedModules = null) {
  return getStepsForRole(role, null, allowedModules).length;
}
